/**
 * Claude tool definitions (Anthropic format) and corresponding
 * JS handler functions that search/filter the in-memory menu data.
 */

// ── Tool Definitions (sent to Claude API) ───────────────────────────

export const toolDefinitions = [
  {
    name: 'search_menu',
    description:
      'Search across all products, submenus, and modifier options by keyword. ' +
      'Returns matching items with their IDs, names, parent info, and type.',
    input_schema: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Search term to match against names (case-insensitive)',
        },
        section: {
          type: 'string',
          description: 'Optional section filter: Food, Drinks, Desserts, or Catering',
        },
        type: {
          type: 'string',
          enum: ['product', 'subMenu', 'modifier'],
          description: 'Optional type filter',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'get_submenu_details',
    description:
      'Get full details of one or more submenus including all their products (name, id, price, draft status).',
    input_schema: {
      type: 'object',
      properties: {
        submenuIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Array of submenu document IDs to retrieve',
        },
      },
      required: ['submenuIds'],
    },
  },
  {
    name: 'get_product_details',
    description:
      'Get full details of one or more products including all modifier groups and options.',
    input_schema: {
      type: 'object',
      properties: {
        productIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Array of product document IDs to retrieve',
        },
      },
      required: ['productIds'],
    },
  },
  {
    name: 'list_submenus',
    description:
      'List all submenus for the shop, optionally filtered by section.',
    input_schema: {
      type: 'object',
      properties: {
        section: {
          type: 'string',
          description: 'Optional section filter: Food, Drinks, Desserts, or Catering',
        },
      },
      required: [],
    },
  },
  {
    name: 'propose_operations',
    description:
      'Propose a list of menu edit operations for the user to review and approve. ' +
      'Call this ONLY after you have fully clarified the request and looked up all relevant data.',
    input_schema: {
      type: 'object',
      properties: {
        understanding: {
          type: 'string',
          description: 'Plain-English summary of what the user wants',
        },
        operations: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              type: {
                type: 'string',
                enum: ['UPDATE_FIELD', 'UPDATE_MODIFIER', 'MOVE_IN_ORDER', 'CREATE_PRODUCT', 'CREATE_SUBMENU'],
                description: 'Operation type',
              },
              docId: {
                type: 'string',
                description: 'Firestore document ID to update. Use "NEW_PRODUCT" to reference a product created by a preceding CREATE_PRODUCT in the same proposal.',
              },
              description: {
                type: 'string',
                description: 'Human-readable description of this change',
              },
              field: {
                type: 'string',
                description: 'Dot-path of the field to update (for UPDATE_FIELD)',
              },
              oldValue: {
                description: 'Current value (for display to user)',
              },
              newValue: {
                description: 'New value to set',
              },
              modgroupId: {
                type: 'string',
                description: 'Modifier group ID (for UPDATE_MODIFIER)',
              },
              modgroupName: {
                type: 'string',
                description: 'Display name for a new modifier group (for add_modifier when the group does not yet exist)',
              },
              modifierId: {
                type: 'string',
                description: 'Modifier option ID to add/remove/edit (for UPDATE_MODIFIER)',
              },
              action: {
                type: 'string',
                enum: ['remove_modifier', 'add_modifier', 'rename_modifier', 'rename_modgroup', 'remove_modgroup'],
                description: 'Modifier action (for UPDATE_MODIFIER)',
              },
              price: {
                type: 'number',
                description: 'Price for the modifier option (for add_modifier action)',
              },
              arrayField: {
                type: 'string',
                description: 'Field containing the array (for MOVE_IN_ORDER)',
              },
              moveId: {
                type: 'string',
                description: 'ID to move within the array (for MOVE_IN_ORDER)',
              },
              toIndex: {
                type: 'number',
                description: '0-based target position in the array (for MOVE_IN_ORDER)',
              },
              subMenuId: {
                type: 'string',
                description: 'Target submenu document ID (for CREATE_PRODUCT)',
              },
              productData: {
                type: 'object',
                description:
                  'Product fields for CREATE_PRODUCT. Include name (object with en key), price (string). Optional: description, code, tags, draft.',
              },
              subMenuData: {
                type: 'object',
                description:
                  'SubMenu fields for CREATE_SUBMENU. Include name (object with en key). Optional: section, description, draft.',
              },
            },
            required: ['type', 'description'],
          },
          description: 'Ordered list of operations to perform',
        },
      },
      required: ['understanding', 'operations'],
    },
  },
]

// ── Helper: extract fields from a Firestore doc-like object ─────────

function getField(doc, field) {
  if (doc.get) return doc.get(field)
  return field.split('.').reduce((obj, key) => obj && obj[key], doc)
}

function nameEn(doc) {
  const n = getField(doc, 'name')
  return (n && n.en) || ''
}

// ── Tool Handlers ───────────────────────────────────────────────────

function handleSearchMenu(shop, { query, section, type }) {
  const q = (query || '').toLowerCase()
  const results = []
  const subMenus = shop.subMenus || []

  for (const sm of subMenus) {
    const smSection = getField(sm, 'section')
    if (section && smSection !== section) continue

    const smName = nameEn(sm)

    if (!type || type === 'subMenu') {
      if (smName.toLowerCase().includes(q)) {
        results.push({
          type: 'subMenu',
          id: sm.id,
          name: smName,
          section: smSection || 'none',
          draft: !!getField(sm, 'draft'),
          productCount: (sm.products || []).length,
        })
      }
    }

    for (const p of sm.products || []) {
      const pName = nameEn(p)

      if (!type || type === 'product') {
        if (pName.toLowerCase().includes(q)) {
          results.push({
            type: 'product',
            id: p.id,
            name: pName,
            price: getField(p, 'price'),
            draft: !!getField(p, 'draft'),
            submenu: smName,
            submenuId: sm.id,
            section: smSection || 'none',
          })
        }
      }

      if (!type || type === 'modifier') {
        const modgroups = getField(p, 'modgroups') || []
        for (const mg of modgroups) {
          for (const mod of mg.modifiers || []) {
            if ((mod.name || '').toLowerCase().includes(q)) {
              results.push({
                type: 'modifier',
                id: mod.id,
                name: mod.name,
                modgroupId: mg.id,
                modgroupName: mg.name,
                parentProductId: p.id,
                parentProductName: pName,
                submenu: smName,
                section: smSection || 'none',
              })
            }
          }
        }
      }
    }
  }

  return results.slice(0, 30)
}

function handleGetSubmenuDetails(shop, { submenuIds }) {
  const subMenus = shop.subMenus || []
  return submenuIds.map(id => {
    const sm = subMenus.find(s => s.id === id)
    if (!sm) return { id, error: 'not found' }
    return {
      id: sm.id,
      name: nameEn(sm),
      section: getField(sm, 'section') || 'none',
      draft: !!getField(sm, 'draft'),
      disableOrder: !!getField(sm, 'disableOrder'),
      description: (getField(sm, 'description') || {}).en || '',
      products: (sm.products || []).map(p => ({
        id: p.id,
        name: nameEn(p),
        price: getField(p, 'price'),
        draft: !!getField(p, 'draft'),
        code: getField(p, 'code') || '',
        hasModifiers: ((getField(p, 'modgroups') || []).length > 0),
      })),
    }
  })
}

function handleGetProductDetails(shop, { productIds }) {
  const allProducts = []
  for (const sm of shop.subMenus || []) {
    for (const p of sm.products || []) {
      allProducts.push({ product: p, submenuId: sm.id, submenuName: nameEn(sm), section: getField(sm, 'section') })
    }
  }

  return productIds.map(id => {
    const found = allProducts.find(e => e.product.id === id)
    if (!found) return { id, error: 'not found' }
    const p = found.product
    return {
      id: p.id,
      name: nameEn(p),
      price: getField(p, 'price'),
      draft: !!getField(p, 'draft'),
      code: getField(p, 'code') || '',
      description: (getField(p, 'description') || {}).en || '',
      tags: getField(p, 'tags') || '',
      submenuId: found.submenuId,
      submenuName: found.submenuName,
      section: found.section || 'none',
      modgroups: (getField(p, 'modgroups') || []).map(mg => ({
        id: mg.id,
        name: mg.name,
        type: mg.type,
        required: !!mg.required,
        modifiers: (mg.modifiers || []).map(m => ({
          id: m.id,
          name: m.name,
          price: m.price,
        })),
      })),
    }
  })
}

function handleListSubmenus(shop, { section }) {
  return (shop.subMenus || [])
    .filter(sm => !section || getField(sm, 'section') === section)
    .map(sm => ({
      id: sm.id,
      name: nameEn(sm),
      section: getField(sm, 'section') || 'none',
      draft: !!getField(sm, 'draft'),
      productCount: (sm.products || []).length,
    }))
}

// ── Dispatcher ──────────────────────────────────────────────────────

const handlers = {
  search_menu: handleSearchMenu,
  get_submenu_details: handleGetSubmenuDetails,
  get_product_details: handleGetProductDetails,
  list_submenus: handleListSubmenus,
  // propose_operations is handled specially by the hook (surfaces to UI)
}

export function executeToolCall(shop, toolName, toolInput) {
  const handler = handlers[toolName]
  if (!handler) return { error: `Unknown tool: ${toolName}` }
  return handler(shop, toolInput)
}
